/**
 * Stepper Component
 *
 * Horizontal step indicator for multi-step flows; wraps on narrow screens.
 */
import * as React from 'react';
import { cn, getTestId, type TestIdProps } from '../utils';
import { useTranslation } from '../providers/UIProvider';
import { Icon } from './icons/Icon';
import type { IconName } from '../icons/names';

/** State of one step in a `Stepper`. */
export const StepState = {
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
  DONE: 'done',
  ERROR: 'error'
} as const;

export type StepState = (typeof StepState)[keyof typeof StepState];

export interface StepperStep {
  id: string;
  label: string;
  /** Badge icon; the step number is shown without one. Done and error steps use their own. */
  icon?: IconName;
  state: StepState;
}

/** Screen-reader text for the done and error states. */
export interface StepperStateLabels {
  done: string;
  error: string;
}

export interface StepperProps extends Pick<TestIdProps, 'testId' | 'id'> {
  steps: readonly StepperStep[];
  /** Makes done and error steps buttons that report their id. */
  onStepClick?: (id: string) => void;
  /** Overrides the `components.stepper` translations. */
  stateLabels?: StepperStateLabels;
  className?: string;
}

const badgeClasses: Record<StepState, string> = {
  [StepState.UPCOMING]: 'bg-background-muted text-foreground-muted',
  [StepState.ACTIVE]: 'bg-primary-base text-white',
  [StepState.DONE]: 'bg-success-bg text-success-text',
  [StepState.ERROR]: 'bg-error-bg text-error-text'
};

const labelClasses: Record<StepState, string> = {
  [StepState.UPCOMING]: 'text-foreground-muted',
  [StepState.ACTIVE]: 'font-semibold text-foreground-base',
  [StepState.DONE]: 'text-foreground-muted',
  [StepState.ERROR]: 'text-error-text'
};

function stateLabel(state: StepState, labels: StepperStateLabels): string | undefined {
  if (state === StepState.DONE) return labels.done;
  if (state === StepState.ERROR) return labels.error;
  return undefined;
}

function badgeIcon(step: StepperStep): IconName | undefined {
  if (step.state === StepState.DONE) return 'check';
  if (step.state === StepState.ERROR) return 'triangle-exclamation';
  return step.icon;
}

export const Stepper = React.forwardRef<HTMLOListElement, StepperProps>(
  ({ steps, onStepClick, stateLabels, className, testId, id }, ref) => {
    const t = useTranslation('components.stepper');
    const labels = stateLabels ?? { done: t('done'), error: t('error') };
    const baseTestId = getTestId({ testId, id }) || undefined;

    return (
      <ol
        ref={ref}
        id={id}
        className={cn('flex flex-wrap items-center gap-sm', className)}
        data-testid={baseTestId}
      >
        {steps.map((step, index) => {
          const icon = badgeIcon(step);
          const srState = stateLabel(step.state, labels);
          const settled = srState !== undefined;
          const content = (
            <>
              <span
                aria-hidden='true'
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  badgeClasses[step.state]
                )}
              >
                {icon ? <Icon name={icon} size='xs' /> : index + 1}
              </span>
              <span className={cn('text-sm', labelClasses[step.state])}>
                {step.label}
                {settled && ' '}
                {settled && <span className='sr-only'>({srState})</span>}
              </span>
            </>
          );

          return (
            <li
              key={step.id}
              className='flex items-center gap-sm'
              aria-current={step.state === StepState.ACTIVE ? 'step' : undefined}
              data-testid={baseTestId && `${baseTestId}-${step.id}`}
              data-state={step.state}
            >
              {settled && onStepClick ? (
                <button
                  type='button'
                  onClick={() => onStepClick(step.id)}
                  className={cn(
                    'flex cursor-pointer items-center gap-sm rounded-md transition-opacity hover:opacity-80',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-offset-2'
                  )}
                >
                  {content}
                </button>
              ) : (
                <span className='flex items-center gap-sm'>{content}</span>
              )}
              {index < steps.length - 1 && (
                <span
                  data-slot='stepper-connector'
                  className='hidden h-px w-8 bg-border-base sm:block'
                  aria-hidden='true'
                />
              )}
            </li>
          );
        })}
      </ol>
    );
  }
);

Stepper.displayName = 'Stepper';
